import React, { useContext, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { FlowScreenWrapper } from "../shared/FlowScreenWrapper";
import { useAuthContext } from "../../../AuthContextWrapper";
import { RadioAndResponseForm } from "../shared/RadioAndResponseForm";
import { OUTCOMES } from "../../../constants";
import { AssetContext } from "../../../AssetContextWrapper";
import { AssetRealEstate } from "../../../models/AssetDetail";
import { estateApi } from "../../../apis";

export enum Outcomes {
  keepItInTheFamily = "Keep it in the family",
  sellIt = "Sell it",
  donateIt = "Donate it",
  notSure = "Not sure yet",
  other = "Other",
  skip = "Skip",
}

export const PropertyPlanScreen = (): JSX.Element => {
  const navigate = useNavigate();
  const { flow, activeEstate, user } = useAuthContext()
  const { assetsLoaded } = useContext(AssetContext);
  const { screenName } = useParams();
  const currentScreen = flow?.getCurrentFlowScreen(screenName);
  const primaryRealEstate = activeEstate?.primaryRealEstate;
  const reAsset = primaryRealEstate?.detail as AssetRealEstate;

  useEffect(() => {
    if (assetsLoaded && !activeEstate?.primaryRealEstate) {
      currentScreen &&
        flow?.navigateToNext(currentScreen, Outcomes.skip, navigate, {
          replace: true,
        });
    }
  }, [assetsLoaded]);

  const handleComplete = async (
    plan: string,
    more?: string
  ): Promise<boolean> => {
    if (!currentScreen) throw Error("couldn't determine currentScreen");

    if (activeEstate?.id && user?.id && activeEstate?.primaryRealEstate?.detail) {
      const thePlan = plan === Outcomes.other && more ? more : plan;

      (activeEstate.primaryRealEstate.detail as AssetRealEstate).future_plan =
        thePlan;
      try {
        await estateApi.saveAsset(primaryRealEstate,activeEstate.id, user.id);
      } catch (error) {
        throw new Error("could not save data");
      }
    }

    flow?.navigateToNext(currentScreen, OUTCOMES.complete, navigate);
    return true;
  };

  const answerOutcomeMatch = (answer?: string): string | undefined => {
    return Object.values(Outcomes).find(
      (outcome: string) => outcome === answer
    );
  };

  // if user previously selected other, their "tell us more" answer will be stored in the db instead
  let initMoreAnswer;
  let initAnswer = reAsset?.future_plan?.toString();
  if (initAnswer !== "" && !answerOutcomeMatch(initAnswer)) {
    initMoreAnswer = initAnswer;
    initAnswer = Outcomes.other;
  }

  return currentScreen ? (
    <FlowScreenWrapper title="What are you planning to do with the home?">
      <RadioAndResponseForm
        initAnswer={initAnswer}
        initMoreAnswer={initMoreAnswer}
        errorText={`Please tell just what you plan to do with the property`}
        options={[
          { value: Outcomes.keepItInTheFamily },
          { value: Outcomes.sellIt },
          { value: Outcomes.donateIt },
          { value: Outcomes.notSure },
          { value: Outcomes.other, followUp: "Tell us more" },
        ]}
        onComplete={handleComplete}
        onBack={() => navigate(-1)}
      />
    </FlowScreenWrapper>
  ) : (
    <></>
  );
};
